import { Typography } from '@mui/material';
import { Box, BoxProps, SxProps, Theme } from '@mui/system';
import { MouseEventHandler } from 'react';

import { XIcon } from '@/components/icons/XIcon';
import { COLORS } from '@/styles/tokens/colors';

import { UnstyledButton } from '../baseInputs/Button/UnstyledButton';

export interface ChipProps extends Omit<BoxProps, 'ref'> {
  typographySx?: SxProps<Theme>;
  label: React.ReactNode;
  onRemove: MouseEventHandler;
  disabled?: boolean;
}

const BORDER_RADIUS = '4px';

export function Chip({
  typographySx: typographySxOverrides = {},
  label,
  onRemove,
  disabled,
  ...props
}: ChipProps) {
  const typographySx: SxProps<Theme> = {
    px: 1,
    borderRadius: BORDER_RADIUS,
    display: 'inline-block',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    color: disabled ? COLORS.GRAY[300] : COLORS.TEXT.PRIMARY,
    ...typographySxOverrides,
  };

  return (
    // Container to ensure that the badge takes up only the space it needs
    // without overflowing its parent
    <Box display="inline-block" sx={{ maxWidth: '100%' }} {...props}>
      <Box
        sx={{
          backgroundColor: COLORS.GRAY[100],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderRadius: BORDER_RADIUS,
          position: 'relative',
        }}
      >
        <Typography variant="body-xs" component="span" sx={typographySx}>
          {label}
        </Typography>
        <RemoveChipButton onClick={onRemove} disabled={disabled} />
      </Box>
    </Box>
  );
}

interface RemoveChipButtonProps {
  onClick?: MouseEventHandler;
  disabled?: boolean;
}

function RemoveChipButton({ onClick, disabled }: RemoveChipButtonProps) {
  return (
    <UnstyledButton
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label="Remove tag items"
      sx={{
        backgroundColor: disabled ? COLORS.GRAY[300] : COLORS.NAVY[700],
        ':hover': {
          backgroundColor: disabled ? COLORS.GRAY[300] : COLORS.GRAY[500],
        },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer',
        borderTopRightRadius: BORDER_RADIUS,
        borderBottomRightRadius: BORDER_RADIUS,
      }}
    >
      <XIcon size={16} sx={{ color: 'white' }} />
    </UnstyledButton>
  );
}
